import React, { useState, memo } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Paper,
  Tooltip,
  Badge,
} from '@mui/material';
import {
  Send as SendIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useViewport } from '../../../shared/hooks/useViewport';

interface ChatInputProps {
  onSendMessage: (message: string) => void;
  onCancel?: () => void;
  disabled?: boolean;
  isStreaming?: boolean;
  queuedCount?: number;
  placeholder?: string;
  /** What this investigation follows, rendered as one row above the box. */
  procedure?: React.ReactNode;
}

const ChatInput = memo<ChatInputProps>(({
  onSendMessage,
  onCancel,
  disabled = false,
  isStreaming = false,
  queuedCount = 0,
  placeholder = "Ask me anything about your knowledge bases...",
  procedure,
}) => {
  const [message, setMessage] = useState('');
  const { isPhone, isTouch } = useViewport();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim() && !disabled) {
      onSendMessage(message.trim());
      setMessage('');
    }
  };

  // A touch keyboard has no Shift, so there Enter keeps its newline and only Send sends.
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !isTouch) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <Paper
      component="form"
      onSubmit={handleSubmit}
      elevation={3}
      sx={{
        p: isPhone ? 1 : 2,
        m: isPhone ? 1 : 2,
        mb: (theme) => `calc(${theme.spacing(isPhone ? 1 : 2)} + env(safe-area-inset-bottom))`,
        bgcolor: 'background.paper',
        borderRadius: 3,
      }}
    >
      {/* The procedure this investigation follows. It stays while a reply streams: what the case
          is working to is status, and hiding it mid-run is what made a running workflow
          invisible exactly when an operator looks for it. */}
      {procedure}

      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
        <TextField
          fullWidth
          multiline
          maxRows={4}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyPress}
          placeholder={placeholder}
          disabled={disabled}
          variant="outlined"
        />

        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {isStreaming ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Tooltip title="Cancel generation">
                <IconButton
                  size="large"
                  onClick={onCancel}
                  sx={{
                    color: 'text.secondary',
                    bgcolor: 'transparent',
                    '&:hover': {
                      color: 'error.main',
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <CancelIcon />
                </IconButton>
              </Tooltip>
              {queuedCount > 0 && (
                <Box sx={{ fontSize: '0.65rem', color: 'text.secondary', mt: -0.5 }}>
                  {queuedCount} queued
                </Box>
              )}
            </Box>
          ) : (
            <Tooltip title={message.trim() ? "Send message" : ""}>
              <span>
                <Badge
                  badgeContent={queuedCount > 0 ? queuedCount : undefined}
                  color="primary"
                  sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', minWidth: 16, height: 16 } }}
                >
                  <IconButton
                    type="submit"
                    size="large"
                    disabled={disabled || !message.trim()}
                    sx={{
                      color: message.trim() ? 'primary.main' : 'text.disabled',
                      bgcolor: 'transparent',
                      '&:hover': {
                        color: 'primary.light',
                        bgcolor: 'action.hover',
                      },
                      '&:disabled': {
                        color: 'text.disabled',
                        bgcolor: 'transparent',
                      },
                    }}
                  >
                    <SendIcon />
                  </IconButton>
                </Badge>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>

      {message.includes('\n') && !isTouch && (
        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <Box
            component="span"
            sx={{
              fontSize: '0.75rem',
              color: 'text.secondary',
              opacity: 0.7,
            }}
          >
            Press Shift+Enter for new line, Enter to send
          </Box>
        </Box>
      )}
    </Paper>
  );
});

ChatInput.displayName = 'ChatInput';

export default ChatInput;